import {
  CATEGORY_ENCADREUR_MEDICAL,
  CATEGORY_ENCADREUR_RELIGIEUX,
  CATEGORY_NON_RESIDENT_PREMIUM,
  CATEGORY_NON_RESIDENT_STANDARD,
  CATEGORY_NON_RESIDENT_TOTAL_SEATS,
  CATEGORY_PREMIUM,
  CATEGORY_STANDARD,
} from 'data';

// Statistics names and view links are part of the shared StatSerializer contract.
export const statValue = (rows, name) =>
  rows?.find((row) => row.name === name && !row.view?.startsWith('/trip/'))?.value;

const normalizeCategoryName = (name) =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[-‐-―]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const CATEGORY_KEYS = {
  standard: normalizeCategoryName(CATEGORY_STANDARD),
  premium: normalizeCategoryName(CATEGORY_PREMIUM),
  nonResidentStandard: normalizeCategoryName(CATEGORY_NON_RESIDENT_STANDARD),
  nonResidentPremium: normalizeCategoryName(CATEGORY_NON_RESIDENT_PREMIUM),
  religiousSupervisor: normalizeCategoryName(CATEGORY_ENCADREUR_RELIGIEUX),
  medicalSupervisor: normalizeCategoryName(CATEGORY_ENCADREUR_MEDICAL),
};

const categoryKey = (name) => {
  const normalized = normalizeCategoryName(name);
  if (/^non residents? standard$/.test(normalized)) return 'nonResidentStandard';
  if (/^non residents? premium$/.test(normalized)) return 'nonResidentPremium';
  if (/^non residents?$/.test(normalized)) return 'legacyNonResident';
  return Object.entries(CATEGORY_KEYS).find(([, value]) => value === normalized)?.[0] || null;
};

const toQuotaCategory = (row) => ({
  id: row.view,
  name: row.name,
  total: row.total,
  occupied: row.value,
  available: row.total - row.value,
});

export const readQuotaStats = (rows) => {
  const national = rows?.find((row) => row.name === 'Quota national' && !row.view);
  const categoryRows = rows?.filter((row) => row.view?.startsWith('/trip/?category=')) || [];
  if (!national || !Number.isFinite(national.value) || !Number.isFinite(national.total)) return null;

  const ordered = new Map();
  const unknown = [];
  for (const row of categoryRows) {
    const key = categoryKey(row.name);
    if (key === 'legacyNonResident') continue;
    if (key && !ordered.has(key)) ordered.set(key, row);
    else unknown.push(row);
  }

  const nonResidentStandard = ordered.get('nonResidentStandard');
  const nonResidentPremium = ordered.get('nonResidentPremium');
  const hasCompleteNonResidentOccupancy = [nonResidentStandard, nonResidentPremium].every((row) =>
    Number.isFinite(row?.value)
  );
  const nonResidentAvailable = hasCompleteNonResidentOccupancy
    ? CATEGORY_NON_RESIDENT_TOTAL_SEATS - nonResidentStandard.value - nonResidentPremium.value
    : null;

  const categories = [];
  for (const key of Object.keys(CATEGORY_KEYS)) {
    if (key === 'nonResidentStandard' || key === 'nonResidentPremium') {
      const row = ordered.get(key);
      categories.push({
        id: row?.view || `missing:${key}`,
        name: key === 'nonResidentStandard' ? CATEGORY_NON_RESIDENT_STANDARD : CATEGORY_NON_RESIDENT_PREMIUM,
        total: CATEGORY_NON_RESIDENT_TOTAL_SEATS,
        occupied: Number.isFinite(row?.value) ? row.value : null,
        available: nonResidentAvailable,
        sharedQuota: true,
      });
      continue;
    }

    const row = ordered.get(key);
    if (row) categories.push(toQuotaCategory(row));
  }

  if (
    ![...categories.filter((category) => !category.sharedQuota), ...unknown].every(
      (row) => Number.isFinite(row.value ?? row.occupied) && Number.isFinite(row.total)
    )
  )
    return null;

  categories.push(...unknown.map(toQuotaCategory));
  return {
    total: national.total,
    occupied: national.value,
    available: national.total - national.value,
    categories,
  };
};

export const readPaidOperations = (rows, includeDepositPaid = false) => {
  const operations = {
    base: statValue(rows, 'Soldés'),
    medically_fit: statValue(rows, 'Aptes avec paiement confirmé'),
    passport_deposited: statValue(rows, 'Passeports déposés avec paiement confirmé'),
  };
  if (includeDepositPaid) operations.deposit_paid = statValue(rows, 'Acompte payé');
  return Object.values(operations).every(Number.isFinite) ? operations : null;
};

export const readFinanceStats = (rows) => {
  const finance = {
    confirmed: rows?.find((row) => row.name === 'Paiements confirmés')?.value,
    pending: rows?.find((row) => row.name === 'Paiements en attente')?.value,
  };
  return Object.values(finance).every(Number.isFinite) ? finance : null;
};

export const readSelfRegistrationStats = (rows) => {
  const summary = {
    accounts: rows?.find((row) => row.name === 'Comptes auto-inscrits')?.value,
    pilgrims: rows?.find((row) => row.name === 'Pèlerins auto-inscrits')?.value,
  };
  return Object.values(summary).every(Number.isFinite) ? summary : null;
};

export const readAgencyQueue = (rows, completedKey) =>
  rows
    .filter((row) => row.view?.startsWith('/trip/?agency='))
    .map((row) => ({
      id: row.view,
      name: row.name,
      [completedKey]: row.value,
      awaiting: row.total - row.value,
    }));
